import { t } from '@lingui/macro';
import type {
  OnDocumentSaveFn,
  OnDocumentSaveFnDocument,
} from 'components/DocumentSidebarForm/DocumentSidebarForm';
import type { DocumentUploadFormValues } from 'forms/document/DocumentUploadForm';
import { hasRenderableErrors, setFormErrors } from 'forms/helpers/setFormErrors';
import { renameFile } from 'helpers/document';
import {
  useCreateDocumentMutation,
  useDeleteFileMutation,
  useRenameFileMutation,
  useUpdateDocumentMutation,
  useUploadFileMutation,
  type Document,
} from 'generated/gql';
import { pick } from 'lodash';
import { useCallback } from 'react';

import { useGlobalError } from './useGlobalError';

interface OnDocumentUpdateCompletedArgs {
  document: OnDocumentSaveFnDocument;
  file: DocumentUploadFormValues['file'];
  onDocumentSave?: OnDocumentSaveFn;
}
export const useUploadDocument = () => {
  const [, setGlobalError] = useGlobalError();
  const [uploadFile] = useUploadFileMutation();

  return useCallback(
    async ({ document, file, onDocumentSave }: OnDocumentUpdateCompletedArgs) => {
      if (file?.content) {
        await uploadFile({
          variables: { file: renameFile(file.content, file.name), documentId: document.id },
          context: { includeTypename: true, useMultipart: true },
          onCompleted: () => {
            if (onDocumentSave) onDocumentSave(document);
          },
          onError: ({ graphQLErrors }) => {
            setFormErrors({
              errors: graphQLErrors,
              setGlobalError,
            });
          },
        });
      }
    },
    [setGlobalError, uploadFile],
  );
};

interface UpdateDocumentArgs {
  values: DocumentUploadFormValues;
  document?: Document;
  onDocumentSave?: OnDocumentSaveFn;
}
export const useUpdateDocument = (registerId: string) => {
  const [, setGlobalError] = useGlobalError();
  const [createDocument] = useCreateDocumentMutation();
  const [updateDocument] = useUpdateDocumentMutation();
  const uploadDocument = useUploadDocument();
  const [deleteFile] = useDeleteFileMutation();
  const [renameUploadedFile] = useRenameFileMutation();
  const errorMessage = t`Het document kon niet worden opgeslagen. Probeer het alstublieft opnieuw.`;

  const onDocumentCompleted = useCallback(
    async ({ document, file, onDocumentSave }: OnDocumentUpdateCompletedArgs) => {
      if (file?.content) {
        uploadDocument({ document, file, onDocumentSave });
      } else {
        if (onDocumentSave) onDocumentSave(document);
      }
    },
    [uploadDocument],
  );

  return useCallback(
    async ({ values, document, onDocumentSave }: UpdateDocumentArgs) => {
      // Clear any previous error.
      setGlobalError(undefined);
      const documentInput = {
        ...pick(values, ['type', 'date', 'commentary', 'deedPassed']),
        labels: values.labels || [],
      };

      if (!document) {
        if (!registerId) return;
        await createDocument({
          variables: {
            registerId,
            document: documentInput,
          },
          onCompleted: ({ createDocument }) => {
            onDocumentCompleted({ document: createDocument, file: values.file, onDocumentSave });
          },
          onError: () => {
            setGlobalError(errorMessage);
          },
        }).catch(() => setGlobalError(errorMessage));
      } else {
        const fileName = values.file?.name?.trim();
        if (document.file && !values.file) {
          await deleteFile({ variables: { documentId: document.id } });
        } else if (
          document.file &&
          !values.file?.content &&
          fileName &&
          fileName !== document.file.name
        ) {
          // Renamed before updating the document: the returned File has no id to normalize on,
          // so the updated name reaches the cache through the updateDocument response instead.
          const { data } = await renameUploadedFile({
            variables: { documentId: document.id, fileName },
            onError: ({ graphQLErrors }) => {
              if (hasRenderableErrors(graphQLErrors))
                setFormErrors({
                  errors: graphQLErrors,
                  setGlobalError,
                  operationName: 'RenameFile',
                });
              else setGlobalError(errorMessage);
            },
          });
          if (!data) return;
        }
        updateDocument({
          variables: { documentId: document.id, document: documentInput },
          onCompleted: ({ updateDocument }) =>
            onDocumentCompleted({ document: updateDocument, file: values.file, onDocumentSave }),
          onError: () => {
            setGlobalError(errorMessage);
          },
        }).catch(() => setGlobalError(errorMessage));
      }
    },
    [
      createDocument,
      deleteFile,
      errorMessage,
      onDocumentCompleted,
      registerId,
      renameUploadedFile,
      setGlobalError,
      updateDocument,
    ],
  );
};
